/**
 * The sea, defined once.
 *
 * A sum of Gerstner waves drives both the ocean mesh (on the GPU, via TSL) and
 * the buoyancy solver (on the CPU, in plain TS). Both read the same wave table
 * and use the same formulation, so a hull floats on the water you can actually
 * see rather than on an approximation of it.
 *
 * Gerstner waves displace horizontally as well as vertically, which is what
 * gives them their sharp crests and broad troughs. The cost is that the surface
 * is no longer a height field: the point that ends up at world (x, z) started
 * somewhere else. `sampleWater` inverts that with a short Newton solve.
 */

import { Vector4 } from 'three'
import { Fn, Loop, cos, cross, float, normalize, sin, uniform, uniformArray, vec3 } from 'three/tsl'
import type { TSLNode } from './tslTypes.ts'

export const GRAVITY = 9.81

export const WAVE_COUNT = 6

/**
 * Each wave is (directionX, directionZ, amplitude, wavelength). Directions are
 * unit vectors in the XZ plane; the set is fanned around a prevailing westerly
 * so the sea has a dominant direction without looking like a corduroy pattern.
 * Wavelengths descend from long swell to short chop.
 */
const WAVE_TABLE: readonly [number, number, number, number][] = [
  [1.0, 0.15, 0.55, 62],
  [0.85, -0.5, 0.34, 41],
  [0.6, 0.8, 0.2, 27],
  [0.95, 0.32, 0.12, 17],
  [0.3, -0.95, 0.07, 11],
  [0.75, 0.66, 0.04, 6.5],
]

export interface Wave {
  /** Unit direction of travel, X component. */
  dx: number
  /** Unit direction of travel, Z component. */
  dz: number
  /** Crest height above the mean surface, in metres. */
  amplitude: number
  /** Angular wavenumber, 2pi / wavelength. */
  k: number
  /** Angular frequency, sqrt(g * k) — the deep-water dispersion relation. */
  omega: number
}

export const waves: Wave[] = WAVE_TABLE.map(([dx, dz, amplitude, wavelength]) => {
  const len = Math.hypot(dx, dz)
  const k = (Math.PI * 2) / wavelength
  return { dx: dx / len, dz: dz / len, amplitude, k, omega: Math.sqrt(GRAVITY * k) }
})

/**
 * Sea state. `swell` scales every amplitude; `steepness` is the total Gerstner
 * Q budget shared across the wave set. Keeping the budget global (rather than
 * per-wave) is what stops the crests from folding over themselves — at 1.0 the
 * sharpest wave is exactly on the verge of pinching.
 */
export const seaState = {
  swell: 1.0,
  steepness: 0.72,
}

// ---------------------------------------------------------------------------
// GPU side
// ---------------------------------------------------------------------------

/** Wave table as it reaches the shader: xy = direction, z = amplitude, w = k. */
const waveData = waves.map((w) => new Vector4(w.dx, w.dz, w.amplitude, w.k))

export const waveArrayUniform = uniformArray(waveData)
export const swellUniform = uniform(seaState.swell)
export const steepnessUniform = uniform(seaState.steepness)
/** Wave clock, in seconds. Advanced by the game loop, not by the renderer. */
export const waveTimeUniform = uniform(0)

export function syncWaveUniforms(elapsed: number): void {
  waveTimeUniform.value = elapsed
  swellUniform.value = seaState.swell
  steepnessUniform.value = seaState.steepness
}

/**
 * Evaluate the sea surface at an undisturbed world XZ.
 *
 * Returns the displacement to add to the base position. Because a TSL function
 * can only return one value, the normal and the foam factor come back through
 * `normalOut` and `foamOut`, which the caller declares with `.toVar()`. Doing
 * all three in one pass matters: the sine and cosine of each wave's phase are
 * shared between the displacement, the analytic tangent frame and the Jacobian.
 *
 *   const n = vec3(0, 1, 0).toVar()
 *   const foam = float(0).toVar()
 *   const offset = gerstnerSurface(basePosition, n, foam)
 *
 * Foam comes from the horizontal Jacobian determinant: where the wave is
 * squeezing neighbouring water together the determinant collapses toward zero,
 * and that is exactly where a crest sharpens and breaks.
 */
export const gerstnerSurface = /*#__PURE__*/ Fn(
  ([basePosition, normalOut, foamOut]: [TSLNode, TSLNode, TSLNode]) => {
    const p = basePosition.xz.toVar()
    const offset = vec3(0).toVar()

    // Tangent along +X and binormal along +Z, accumulated analytically so the
    // normal stays exact no matter how coarse the mesh gets.
    const tangent = vec3(1, 0, 0).toVar()
    const binormal = vec3(0, 0, 1).toVar()

    // Horizontal Jacobian, for the foam term.
    const jxx = float(1).toVar()
    const jzz = float(1).toVar()
    const jxz = float(0).toVar()

    const qBudget: TSLNode = steepnessUniform.div(float(WAVE_COUNT))

    Loop(WAVE_COUNT, ({ i }) => {
      const w: TSLNode = waveArrayUniform.element(i)
      const dir = w.xy
      const amplitude = w.z.mul(swellUniform)
      const k = w.w

      const omega = k.mul(GRAVITY).sqrt()
      const phase = k.mul(dir.dot(p)).sub(omega.mul(waveTimeUniform))
      const s = sin(phase)
      const c = cos(phase)

      // Q is expressed against k*A so that steepness is scale-invariant: a long
      // swell and a short chop pinch at the same visual sharpness.
      const q = qBudget.div(k.mul(amplitude).max(1e-4))
      const wa = k.mul(amplitude)
      const qwa = q.mul(wa)

      offset.addAssign(vec3(q.mul(amplitude).mul(dir.x).mul(c), amplitude.mul(s), q.mul(amplitude).mul(dir.y).mul(c)))

      tangent.addAssign(
        vec3(qwa.mul(dir.x).mul(dir.x).mul(s).negate(), dir.x.mul(wa).mul(c), qwa.mul(dir.x).mul(dir.y).mul(s).negate()),
      )
      binormal.addAssign(
        vec3(qwa.mul(dir.x).mul(dir.y).mul(s).negate(), dir.y.mul(wa).mul(c), qwa.mul(dir.y).mul(dir.y).mul(s).negate()),
      )

      jxx.subAssign(qwa.mul(dir.x).mul(dir.x).mul(s))
      jzz.subAssign(qwa.mul(dir.y).mul(dir.y).mul(s))
      jxz.subAssign(qwa.mul(dir.x).mul(dir.y).mul(s))
    })

    normalOut.assign(normalize(cross(binormal, tangent)))

    const determinant = jxx.mul(jzz).sub(jxz.mul(jxz))
    foamOut.assign(determinant.oneMinus().smoothstep(0.25, 0.95))

    return offset
  },
)

// ---------------------------------------------------------------------------
// CPU side
// ---------------------------------------------------------------------------

export interface WaterSample {
  /** Surface height at the queried world XZ. */
  height: number
  /** Surface normal, unit length. */
  normalX: number
  normalY: number
  normalZ: number
  /** Rate of change of height at a fixed world XZ, m/s. */
  verticalVelocity: number
}

const scratchSample: WaterSample = { height: 0, normalX: 0, normalY: 1, normalZ: 0, verticalVelocity: 0 }

/**
 * Sample the sea surface at a world XZ.
 *
 * Because Gerstner waves move water horizontally, the surface point that *ends
 * up* at (x, z) originated at some other (x0, z0). Three Newton iterations
 * recover it accurately even at the steep storm crests. A fixed-point solve
 * converges too slowly there, putting buoyancy behind the visible wave.
 *
 * The returned object is reused between calls. Copy anything you need to keep.
 */
export function sampleWater(x: number, z: number, t: number): WaterSample {
  const qBudget = seaState.steepness / WAVE_COUNT

  // Recover the source point by undoing the horizontal displacement.
  let sx = x
  let sz = z
  for (let iter = 0; iter < 3; iter++) {
    let ox = 0
    let oz = 0
    let jxx = 1
    let jxz = 0
    let jzz = 1
    for (let i = 0; i < waves.length; i++) {
      const w = waves[i]
      const amplitude = w.amplitude * seaState.swell
      const q = qBudget / Math.max(w.k * amplitude, 1e-4)
      const phase = w.k * (w.dx * sx + w.dz * sz) - w.omega * t
      const c = Math.cos(phase)
      const slope = q * amplitude * w.k * Math.sin(phase)
      ox += q * amplitude * w.dx * c
      oz += q * amplitude * w.dz * c
      jxx -= slope * w.dx * w.dx
      jxz -= slope * w.dx * w.dz
      jzz -= slope * w.dz * w.dz
    }
    const rx = sx + ox - x
    const rz = sz + oz - z
    const determinant = jxx * jzz - jxz * jxz
    sx -= (jzz * rx - jxz * rz) / determinant
    sz -= (jxx * rz - jxz * rx) / determinant
  }

  // Evaluate height, normal and vertical rate at the recovered source point.
  let height = 0
  let verticalVelocity = 0
  let horizontalVelocityX = 0
  let horizontalVelocityZ = 0
  let tx = 1
  let ty = 0
  let tz = 0
  let bx = 0
  let by = 0
  let bz = 1

  for (let i = 0; i < waves.length; i++) {
    const w = waves[i]
    const amplitude = w.amplitude * seaState.swell
    const q = qBudget / Math.max(w.k * amplitude, 1e-4)
    const phase = w.k * (w.dx * sx + w.dz * sz) - w.omega * t
    const s = Math.sin(phase)
    const c = Math.cos(phase)

    height += amplitude * s
    verticalVelocity += -amplitude * w.omega * c
    horizontalVelocityX += q * amplitude * w.dx * w.omega * s
    horizontalVelocityZ += q * amplitude * w.dz * w.omega * s

    const wa = w.k * amplitude
    tx -= q * w.dx * w.dx * wa * s
    ty += w.dx * wa * c
    tz -= q * w.dx * w.dz * wa * s

    bx -= q * w.dx * w.dz * wa * s
    by += w.dz * wa * c
    bz -= q * w.dz * w.dz * wa * s
  }

  // normal = normalize(cross(binormal, tangent))
  let nx = by * tz - bz * ty
  let ny = bz * tx - bx * tz
  let nz = bx * ty - by * tx
  // The water particles also move sideways. Convert their vertical velocity
  // to the height change at the queried world location before normalizing.
  verticalVelocity += (nx * horizontalVelocityX + nz * horizontalVelocityZ) / Math.max(ny, 1e-4)
  const invLen = 1 / (Math.hypot(nx, ny, nz) || 1)
  nx *= invLen
  ny *= invLen
  nz *= invLen

  scratchSample.height = height
  scratchSample.normalX = nx
  scratchSample.normalY = ny
  scratchSample.normalZ = nz
  scratchSample.verticalVelocity = verticalVelocity
  return scratchSample
}

/** Height only — skips the normal accumulation when that is all you need. */
export function sampleWaterHeight(x: number, z: number, t: number): number {
  return sampleWater(x, z, t).height
}
